import { Link } from "react-router-dom";
import type { Article } from "@/types/content";
import { formatViews } from "@/utils/content";

interface TrendingSectionProps {
  trending: Article[];
  latest: Article[];
}

export default function TrendingSection({ trending, latest }: TrendingSectionProps) {
  const maxHeat = trending.length ? trending[0].heat : 100;

  return (
    <section className="max-w-portal mx-auto px-4 md:px-6 pt-12 md:pt-16">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
        {/* 今日热榜 */}
        <div className="lg:col-span-3 rounded-lg border-2 border-foreground-950 bg-background-100 p-5 md:p-7">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-9 h-9 flex items-center justify-center rounded bg-accent-500 text-background-50">
              <i className="ri-fire-line text-xl" />
            </span>
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950">
                今日热榜
              </h2>
              <p className="text-xs text-foreground-500 mt-0.5">按现场热度实时排序</p>
            </div>
          </div>
          <ul>
            {trending.map((item, idx) => (
              <li key={item.id} className="group">
                <Link
                  to={`/article/${item.slug}`}
                  className="flex items-center gap-4 py-3.5 border-b border-background-200 last:border-0 cursor-pointer"
                >
                  <span
                    className={`w-7 h-7 shrink-0 flex items-center justify-center rounded font-heading font-black text-sm ${
                      idx < 3
                        ? "bg-accent-500 text-background-50"
                        : "bg-background-200 text-foreground-700"
                    }`}
                  >
                    {idx + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm md:text-base font-bold text-foreground-900 leading-snug line-clamp-1 group-hover:text-accent-700 transition-colors">
                      {item.title}
                    </h3>
                    <div className="flex items-center gap-3 mt-1 text-xs text-foreground-500">
                      <span className="text-secondary-700">{item.categoryName}</span>
                      <span className="inline-flex items-center gap-1">
                        <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center" />
                        {formatViews(item.views)}
                      </span>
                    </div>
                  </div>
                  <div className="hidden sm:flex items-center gap-2 w-28 shrink-0">
                    <div className="flex-1 h-1.5 rounded-full bg-background-200 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary-500"
                        style={{ width: `${Math.round((item.heat / maxHeat) * 100)}%` }}
                      />
                    </div>
                    <span className="text-xs font-bold text-primary-700 w-6 text-right">
                      {item.heat}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* 最新爆料 */}
        <div className="lg:col-span-2 rounded-lg bg-secondary-900 text-background-100 p-5 md:p-7">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-black">最新爆料</h2>
            <Link
              to="/category/backstage"
              className="text-xs text-primary-300 hover:text-primary-200 transition-colors cursor-pointer"
            >
              全部 &gt;
            </Link>
          </div>
          <ul className="space-y-4">
            {latest.map((item) => (
              <li key={item.id}>
                <Link to={`/article/${item.slug}`} className="group flex gap-3 cursor-pointer">
                  <span className="w-16 h-16 shrink-0 rounded overflow-hidden bg-secondary-800">
                    <img
                      src={item.cover}
                      alt={`${item.title} 幕后花絮缩略图`}
                      title={item.title}
                      className="w-full h-full object-top object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold leading-snug line-clamp-2 group-hover:text-primary-300 transition-colors">
                      {item.title}
                    </h3>
                    <span className="inline-block text-xs text-background-400 mt-1">
                      {item.date} · {item.readTime}
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}